import type { CountUnit } from './types.js';

// TextEncoder is in both Node and the browser, so the server and the live
// preview measure identically — which PLAN §11.6 requires them to.
const encoder = new TextEncoder();

/** UTF-8 bytes. YouTube's 5,000-character description limit is really bytes. */
export function byteLength(value: string): number {
  return encoder.encode(value).length;
}

/** UTF-16 code units — what `String.length` returns, and what TikTok counts. */
export function utf16Length(value: string): number {
  return value.length;
}

/**
 * What a reader would count: "é" is one, and so is a flag or a family emoji
 * that costs several code units. Intl.Segmenter is widely available; the
 * fallback counts code points, which is still closer than code units.
 */
export function graphemeLength(value: string): number {
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    let count = 0;
    for (const _ of segmenter.segment(value)) count += 1;
    return count;
  }
  return [...value].length;
}

export function measure(value: string, unit: CountUnit): number {
  switch (unit) {
    case 'bytes':
      return byteLength(value);
    case 'utf16':
      return utf16Length(value);
    case 'chars':
      return graphemeLength(value);
  }
}

/**
 * Cut to at most `max` units without splitting a surrogate pair or a combining
 * sequence — slicing a string by byte count is how you get a replacement
 * character at the end of a caption.
 */
export function truncateTo(value: string, max: number, unit: CountUnit): string {
  if (measure(value, unit) <= max) return value;

  let result = '';
  for (const segment of segments(value)) {
    const next = result + segment;
    if (measure(next, unit) > max) break;
    result = next;
  }
  return result;
}

/** Cut at the last whole word that fits, so a title never ends mid-word. */
export function truncateAtWord(value: string, max: number, unit: CountUnit): string {
  if (measure(value, unit) <= max) return value;

  const hard = truncateTo(value, max, unit);
  const lastSpace = hard.lastIndexOf(' ');
  // A single word longer than the limit has no boundary to fall back to.
  return (lastSpace > 0 ? hard.slice(0, lastSpace) : hard).trimEnd();
}

function segments(value: string): Iterable<string> {
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    return (function* iterate() {
      for (const { segment } of segmenter.segment(value)) yield segment;
    })();
  }
  return [...value];
}
